import { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Radio, Row, Select, Space, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  BROADCAST_FILTER_FIELDS,
  BROADCAST_FILTER_MAX_CONDITIONS,
  BROADCAST_KYC_FILTER_VALUES,
  BROADCAST_PLANS,
  BroadcastFilter,
  BroadcastFilterCombinator,
  BroadcastFilterCondition,
  BroadcastFilterField,
  BroadcastKycFilterValue,
  BroadcastPlan,
  TRADEVEST_PROVISIONING_STATUSES,
  TradevestProvisioningStatus,
  UserStatus,
} from '@mygate/common';
import {
  FILTER_COMBINATOR_LABELS,
  FILTER_FIELD_LABELS,
  KYC_FILTER_VALUE_LABELS,
  PLAN_LABELS,
  USER_STATUS_LABELS,
  humanizeTradevestStatus,
} from './constants';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type MultiValueField = 'userStatus' | 'kycStatus' | 'tradevestProvisioningStatus' | 'paycenterPlan';

interface MultiValueRow {
  id: string;
  field: MultiValueField;
  values: string[];
}
interface BooleanRow {
  id: string;
  field: 'hasTradevestAccount';
  value: boolean | null;
}
interface DateRow {
  id: string;
  field: 'createdAt' | 'lastLoginAt';
  from?: string;
  to?: string;
}
type FilterRow = MultiValueRow | BooleanRow | DateRow;

function emptyRowForField(field: BroadcastFilterField, id: string): FilterRow {
  if (field === 'hasTradevestAccount') return { id, field, value: null };
  if (field === 'createdAt' || field === 'lastLoginAt') return { id, field, from: undefined, to: undefined };
  return { id, field, values: [] };
}

let rowIdCounter = 0;
function nextRowId(): string {
  rowIdCounter += 1;
  return `row-${rowIdCounter}`;
}

/** Seeds the wizard's default audience: today's legacy behavior (status ACTIVE only). */
export function defaultBroadcastFilter(): BroadcastFilter {
  return {
    combinator: BroadcastFilterCombinator.AND,
    conditions: [{ field: 'userStatus', values: [UserStatus.ACTIVE] }],
  };
}

function conditionToRow(condition: BroadcastFilterCondition): FilterRow {
  const id = nextRowId();
  switch (condition.field) {
    case 'userStatus':
      return { id, field: 'userStatus', values: condition.values };
    case 'kycStatus':
      return { id, field: 'kycStatus', values: condition.values };
    case 'tradevestProvisioningStatus':
      return { id, field: 'tradevestProvisioningStatus', values: condition.values };
    case 'paycenterPlan':
      return { id, field: 'paycenterPlan', values: condition.values };
    case 'hasTradevestAccount':
      return { id, field: 'hasTradevestAccount', value: condition.value };
    case 'createdAt':
    case 'lastLoginAt':
      return { id, field: condition.field, from: condition.from, to: condition.to };
  }
}

/** Stable order so filtersEqual (JSON comparison) can't report a false "dirty" after reorder-only edits. */
function sortedValues(values: string[]): string[] {
  return [...values].sort();
}

function rowToCondition(row: FilterRow): BroadcastFilterCondition | undefined {
  switch (row.field) {
    case 'userStatus':
      return row.values.length ? { field: 'userStatus', values: sortedValues(row.values) as UserStatus[] } : undefined;
    case 'kycStatus':
      return row.values.length
        ? { field: 'kycStatus', values: sortedValues(row.values) as BroadcastKycFilterValue[] }
        : undefined;
    case 'tradevestProvisioningStatus':
      return row.values.length
        ? { field: 'tradevestProvisioningStatus', values: sortedValues(row.values) as TradevestProvisioningStatus[] }
        : undefined;
    case 'paycenterPlan':
      return row.values.length
        ? { field: 'paycenterPlan', values: sortedValues(row.values) as BroadcastPlan[] }
        : undefined;
    case 'hasTradevestAccount':
      return row.value !== null ? { field: 'hasTradevestAccount', value: row.value } : undefined;
    case 'createdAt':
    case 'lastLoginAt':
      return row.from || row.to ? { field: row.field, from: row.from, to: row.to } : undefined;
  }
}

function buildFilter(combinator: BroadcastFilterCombinator, rows: FilterRow[]): BroadcastFilter | undefined {
  const conditions = rows.map(rowToCondition).filter((c): c is BroadcastFilterCondition => c !== undefined);
  return conditions.length > 0 ? { combinator, conditions } : undefined;
}

// ─── Human-readable summary (reused in the Confirm step and the detail drawer) ───

function describeMultiValueCondition(field: MultiValueField, values: readonly string[]): string {
  const labels =
    field === 'userStatus'
      ? values.map(v => USER_STATUS_LABELS[v as UserStatus] ?? v)
      : field === 'kycStatus'
        ? values.map(v => KYC_FILTER_VALUE_LABELS[v as BroadcastKycFilterValue] ?? v)
        : field === 'paycenterPlan'
          ? values.map(v => PLAN_LABELS[v as BroadcastPlan] ?? v)
          : values.map(v => humanizeTradevestStatus(v));
  return `${FILTER_FIELD_LABELS[field]} ∈ ${labels.join(', ')}`;
}

function describeCondition(condition: BroadcastFilterCondition): string {
  switch (condition.field) {
    case 'userStatus':
    case 'kycStatus':
    case 'tradevestProvisioningStatus':
    case 'paycenterPlan':
      return describeMultiValueCondition(condition.field, condition.values);
    case 'hasTradevestAccount':
      return `${FILTER_FIELD_LABELS[condition.field]} = ${condition.value ? 'Has account' : 'No account'}`;
    case 'createdAt':
    case 'lastLoginAt': {
      const from = condition.from ? dayjs(condition.from).format('YYYY-MM-DD') : '…';
      const to = condition.to ? dayjs(condition.to).format('YYYY-MM-DD') : '…';
      return `${FILTER_FIELD_LABELS[condition.field]} ${from} → ${to}`;
    }
    default:
      // Stored broadcasts may carry fields introduced by a newer backend — render the raw name, never a blank row
      return (condition as { field: string }).field;
  }
}

/** Plain-text summary lines for a filter — one entry per condition. Empty array means the default audience. */
export function summarizeBroadcastFilter(filter?: BroadcastFilter): string[] {
  if (!filter || filter.conditions.length === 0) return [];
  return filter.conditions.map(describeCondition);
}

interface FilterSummaryProps {
  filter?: BroadcastFilter;
  emptyText?: string;
}

/** Compact human-readable rendering of a BroadcastFilter — used in the Confirm step and the detail drawer. */
export function FilterSummary({ filter, emptyText = 'Default audience (active users)' }: FilterSummaryProps) {
  const lines = summarizeBroadcastFilter(filter);
  if (lines.length === 0 || !filter) {
    return <Text type="secondary">{emptyText}</Text>;
  }
  return (
    <div>
      <Text type="secondary">{FILTER_COMBINATOR_LABELS[filter.combinator]}:</Text>
      <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
        {lines.map((line, index) => (
          <li key={index}>
            <Text>{line}</Text>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Range presets ───

// Lazy values: computed at click time, not module load — a backoffice tab can stay open for days
const RANGE_PRESETS: Array<{ label: string; value: () => [Dayjs, Dayjs] }> = [
  { label: 'Last 7 days', value: () => [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
  { label: 'Last 30 days', value: () => [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
  { label: 'Last 90 days', value: () => [dayjs().subtract(89, 'day').startOf('day'), dayjs().endOf('day')] },
];

function multiValueOptions(field: MultiValueField): Array<{ value: string; label: string }> {
  switch (field) {
    case 'userStatus':
      return Object.values(UserStatus).map(value => ({ value, label: USER_STATUS_LABELS[value] }));
    case 'kycStatus':
      return BROADCAST_KYC_FILTER_VALUES.map(value => ({ value, label: KYC_FILTER_VALUE_LABELS[value] }));
    case 'paycenterPlan':
      return BROADCAST_PLANS.map(value => ({ value, label: PLAN_LABELS[value] }));
    case 'tradevestProvisioningStatus':
      return TRADEVEST_PROVISIONING_STATUSES.map(value => ({ value, label: humanizeTradevestStatus(value) }));
  }
}

// ─── Per-row condition editor (switch narrows FilterRow reliably; a ternary chain does not) ───

interface ConditionEditorProps {
  row: FilterRow;
  onUpdate: (patch: Partial<FilterRow>) => void;
}

function ConditionEditor({ row, onUpdate }: ConditionEditorProps) {
  switch (row.field) {
    case 'hasTradevestAccount':
      return (
        <Radio.Group
          value={row.value === null ? undefined : row.value ? 'yes' : 'no'}
          onChange={e => onUpdate({ value: e.target.value === 'yes' })}
          optionType="button"
          options={[
            { value: 'yes', label: 'Has account' },
            { value: 'no', label: 'No account' },
          ]}
        />
      );
    case 'createdAt':
    case 'lastLoginAt':
      return (
        <RangePicker
          style={{ width: '100%' }}
          presets={RANGE_PRESETS}
          allowEmpty={[true, true]}
          value={[row.from ? dayjs(row.from) : null, row.to ? dayjs(row.to) : null]}
          onChange={values => {
            const [start, end] = values ?? [null, null];
            onUpdate({
              from: start ? start.startOf('day').toISOString() : undefined,
              to: end ? end.endOf('day').toISOString() : undefined,
            });
          }}
        />
      );
    case 'userStatus':
    case 'kycStatus':
    case 'tradevestProvisioningStatus':
    case 'paycenterPlan':
      return (
        <Select
          mode="multiple"
          showSearch
          allowClear
          value={row.values}
          onChange={values => onUpdate({ values })}
          options={multiValueOptions(row.field)}
          optionFilterProp="label"
          placeholder="Select values"
          style={{ width: '100%' }}
        />
      );
  }
}

// ─── Component ───

interface FilterBuilderProps {
  /** Seeds the initial rows once, on mount — the builder owns its own draft state after that. */
  initialFilter: BroadcastFilter;
  onChange: (filter: BroadcastFilter | undefined) => void;
}

export function FilterBuilder({ initialFilter, onChange }: FilterBuilderProps) {
  const [combinator, setCombinator] = useState<BroadcastFilterCombinator>(initialFilter.combinator);
  const [rows, setRows] = useState<FilterRow[]>(() => initialFilter.conditions.map(conditionToRow));

  // Deliberately omits `onChange` from deps — it's expected to be a stable/inline callback from the
  // parent and re-running on every parent render would defeat the "own draft state" contract.
  useEffect(() => {
    onChange(buildFilter(combinator, rows));
  }, [combinator, rows]);

  const usedFields = new Set(rows.map(row => row.field));
  const availableFields = BROADCAST_FILTER_FIELDS.filter(field => !usedFields.has(field));

  // ConditionEditor only ever produces a patch matching the row's own (switch-narrowed) shape, so this
  // merge is safe at runtime even though the static Partial<FilterRow> type is wider than any one row.
  const updateRow = (id: string, patch: Partial<FilterRow>) => {
    setRows(prev => prev.map(row => (row.id === id ? ({ ...row, ...patch } as FilterRow) : row)));
  };

  const changeRowField = (id: string, field: BroadcastFilterField) => {
    setRows(prev => prev.map(row => (row.id === id ? emptyRowForField(field, id) : row)));
  };

  const removeRow = (id: string) => {
    setRows(prev => prev.filter(row => row.id !== id));
  };

  const addRow = () => {
    if (rows.length >= BROADCAST_FILTER_MAX_CONDITIONS || availableFields.length === 0) return;
    setRows(prev => [...prev, emptyRowForField(availableFields[0], nextRowId())]);
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Radio.Group
          value={combinator}
          onChange={e => setCombinator(e.target.value as BroadcastFilterCombinator)}
          options={Object.values(BroadcastFilterCombinator).map(value => ({
            value,
            label: FILTER_COMBINATOR_LABELS[value],
          }))}
          optionType="button"
        />

        {rows.map(row => {
          const fieldOptions = BROADCAST_FILTER_FIELDS.filter(
            field => field === row.field || availableFields.includes(field),
          ).map(field => ({ value: field, label: FILTER_FIELD_LABELS[field] }));

          return (
            <Row key={row.id} gutter={12} align="middle" wrap={false}>
              <Col flex="240px">
                <Select
                  value={row.field}
                  onChange={value => changeRowField(row.id, value as BroadcastFilterField)}
                  options={fieldOptions}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col flex="auto">
                <ConditionEditor row={row} onUpdate={patch => updateRow(row.id, patch)} />
              </Col>
              <Col flex="32px">
                <Button
                  type="text"
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => removeRow(row.id)}
                  aria-label="Remove condition"
                />
              </Col>
            </Row>
          );
        })}

        <Button
          icon={<PlusOutlined />}
          onClick={addRow}
          disabled={rows.length >= BROADCAST_FILTER_MAX_CONDITIONS || availableFields.length === 0}
        >
          Add condition
        </Button>
      </Space>
    </div>
  );
}
